import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RequestService, LayoutUtilsService, LoaderService } from '../../../shared/services';
import { environment } from '../../../../environments/environment';
import { Subscription } from 'rxjs';
import { ConfirmEntityDialogComponent } from '../modals/confirm-entity-dialog/confirm-entity-dialog.component';
import { ModalPasswordDialogComponent } from '../../../shared/components/custom-password-dialog/custom-password-dialog.component';
import { ModalDialogComponent } from '../custom-dialog/custom-dialog.component';
import { ModalCropDialogComponent } from '../custom-crop-dialog/custom-crop-dialog.component';

@Component({
	selector: 'app-user-view-dialog-modal',
	standalone: true,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		MatIconModule,
		TranslateModule
		// Note: ConfirmEntityDialogComponent, ModalPasswordDialogComponent, ModalDialogComponent, 
		// and ModalCropDialogComponent are opened dynamically via MatDialog.open() 
		// and don't need to be in the imports array
	],
	templateUrl: './custom-user-view-dialog.component.html',
	styleUrls: ['./custom-user-view-dialog.component.scss']
})
export class ModalUserViewDialogComponent implements OnInit {
	private subscriptions: Subscription[] = [];
	public errorMessage: string = '';
	public orgId = undefined;
	public scopes: string[] = [];
	public loading: boolean = false;
	public dataType: any = 'user'
	public hasFormErrors: boolean = false;
	public pictureLink: string = 'assets/images/profile.png';
	public permission: any[] = [];
	public metaFieldTabsSetting = [];
	public tableDataTemp: any = undefined;
	public DND: boolean = false;
	public normalDisplay: string[] = ['string', 'integer', 'phone', 'number', 'email', 'textarea'];
	
	// FROZEN DIALOG VIEW MODEL - All booleans default to false, never mutated after initialization
	// All values derived ONCE from MAT_DIALOG_DATA synchronously
	public hasData: boolean = false;
	public isAnonymous: boolean = false;
	public isProfile: boolean = false;
	public hasModalSetting: boolean = false;
	public hasFooter: boolean = false;
	public hasFieldsDetails: boolean = false;
	public hasOrganization: boolean = false;
	public hasConfirmData: boolean = false;
	/** Single gate: true only when all data needed for the view is set (constructor only). Avoids NG0100. */
	public dialogReady: boolean = false;
	
	// Data properties - initialized synchronously, never mutated to undefined/null after first assignment
	public dialogData: any = undefined;
	public modalSetting: any = undefined;
	public fieldsDetails: any = undefined;
	public organization: any = undefined;
	public confirmData: any = undefined;
	/** Frozen list for *ngFor; set only in constructor to avoid NG0100 when loadData() updates confirmData. */
	public confirmDataResources: any[] = [];
	
	constructor(
		public dialog: MatDialog,
		private translate: TranslateService,
		private requestService: RequestService,
		private layoutUtilsService: LayoutUtilsService,
		public dialogRef: MatDialogRef<ModalUserViewDialogComponent>,
		private loaderService: LoaderService,
		private changeDetectorRefs: ChangeDetectorRef,
		@Inject(MAT_DIALOG_DATA) public data: any) {
		// Extract ALL values from MAT_DIALOG_DATA synchronously in constructor
		// This creates a frozen view model that prevents NG0100
		if (data) {
			this.hasData = true;
			this.dialogData = data.data || undefined;
			this.isAnonymous = data.data?.isAnonymous === true;
			this.isProfile = data.profile === true;
			this.dataType = data.dataType || 'user';
			this.modalSetting = data.modalSetting || undefined;
			this.hasModalSetting = !!this.modalSetting;
			this.hasFooter = this.modalSetting?.hasOwnProperty('footer') === true;
			// Set confirmData / hasConfirmData / isAnonymous from passed data so they are never mutated after (avoids NG0100)
			if (data.data && typeof data.data === 'object' && (data.data._id != null || data.data.firstName != null || data.data.email != null)) {
				this.confirmData = Object.assign({}, data.data);
				this.hasConfirmData = true;
				this.isAnonymous = data.data.isAnonymous === true;
				this.confirmDataResources = Array.isArray(this.confirmData?.['resources']) ? [...this.confirmData['resources']] : [];
				if (data.data.pictureLink) {
					this.pictureLink = data.data.pictureLink;
				}
			}
			// Set fieldsDetails / hasFieldsDetails from modalSetting so they are never mutated after (avoids NG0100)
			if (this.modalSetting && this.modalSetting.fields) {
				this.fieldsDetails = this.buildMetaSetting(this.modalSetting);
				this.hasFieldsDetails = true;
			}
			// Initialize organization synchronously if available from requestService
			const currentOrg = this.requestService.pageOrganization?.value;
			if (currentOrg) {
				this.organization = currentOrg;
				this.orgId = currentOrg._id;
				this.hasOrganization = true;
			}
			// Single gate: show UI only when all required data is present (no async mutations = no NG0100)
			this.dialogReady =
				this.hasData &&
				this.hasModalSetting &&
				this.hasFieldsDetails &&
				(this.hasConfirmData || this.isAnonymous);
		}
	}

	ngOnInit() {
		// Defer subscription callback to avoid NG0100 when BehaviorSubject emits synchronously
		// Use Promise.resolve().then() for Angular 21 microtask deferral
		Promise.resolve().then(() => {
			this.subscriptions.push(
				this.requestService.pageOrganization.subscribe((data) => {
					if (data) {
						// Do NOT set hasOrganization here - it is set only in constructor to avoid NG0100.
						// Update organization/orgId for internal use only (e.g. loadData, display).
						this.organization = data;
						this.orgId = data._id;
						this.buildSetting();
					}
				})
			);
		});
	}
	ngOnDestroy() {
		this.subscriptions.forEach((s) => s.unsubscribe());
	}

	changePassword() {
		const dialogRef = this.dialog.open(ModalPasswordDialogComponent, {
			width: '550px',
			disableClose: true,
			data: {
				title: this.translate.instant('Change Password')
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			// console.log('afterClosed', result);
			if (result) {
				//this.validateUser(userData);
			}
		});
	}
	private buildSetting() {
		if (!this.loading) {
			this.DND = JSON.parse(localStorage.getItem("DND"));
			this.loading = true;
			this.loaderService.display(true);
			this.errorMessage = '';
			this.requestService.getMetaData(this.dataType, undefined, (data, error) => {
				if (error) {
					this.errorMessage = error;
					this.layoutUtilsService.showNotification(this.translate.instant('Error:') + error, this.translate.instant('Dismiss'));
				}
				this.loading = false;
				this.loaderService.display(false);
				if (data) {
					// Do NOT mutate hasModalSetting, hasFooter, or hasFieldsDetails here - set only in constructor.
					// Caller must pass modalSetting (with fields) when opening so hasFieldsDetails is true from start.
					this.modalSetting = data.results;
					this.data.modalSetting = data.results;
					this.fieldsDetails = this.buildMetaSetting(data.results);
					if (this.dialogData && this.dialogData.hasOwnProperty('_id')) {
						this.loadData();
					}
				} else {
					this.layoutUtilsService.showNotification(this.translate.instant('Something is Wrong'), this.translate.instant('Dismiss'));
				}
			});
		}
	}
	public loadData() {
		if (!this.loading) {
			this.loading = true;
			this.loaderService.display(true);
			this.errorMessage = '';
			var isGetValue = false;
			let dataId = this.dialogData?.['_id'];

			if (this.dataType == 'user') {
				isGetValue = true;
			}
			this.requestService.getSingleData(this.dataType, dataId, (data, error) => {
				if (error) {
					this.errorMessage = error;
					this.layoutUtilsService.showNotification(this.translate.instant('Error:') + error, this.translate.instant('Dismiss'));
				}
				this.loading = false;
				this.loaderService.display(false);
				if (data) {
					this.tableDataTemp = Object.assign({}, data.results);
					// Do NOT mutate hasConfirmData or isAnonymous - they are set only in constructor from MAT_DIALOG_DATA.
					// Caller must pass full user in data.data when opening so hasConfirmData is true from start.
					this.confirmData = Object.assign({}, data.results);
					this.data.confirmData = Object.assign({}, data.results);
					if (data.results['pictureLink']) {
						const newLink = data.results['pictureLink'];
						// Defer src change to the next microtask to avoid NG0100
						queueMicrotask(() => {
							this.pictureLink = newLink;
							this.changeDetectorRefs.detectChanges();
						});
					}
				}
			});
		}
	}
	convertArrayToObject(arr: any[]) {
		let obj = {};
		for (let a of arr) {
			obj[a._id] = a;
		}
		return obj;
	}
	closeModal(data): void {
		this.dialogRef.close(data);
	}
	buildMetaSetting(data, parent = undefined) {
		let dataObject = {};
		let tabObject = [];
		for (let col of data.fields) {
			if ((col.editable || !col.generated) && col.type !== 'object' && col.type !== 'table') {
				if (parent) {
					col['inputName'] = parent + col['name'];
				}
				dataObject[col.name] = col;
			} else if (col.type === 'object') {
				dataObject[col.name] = this.buildMetaSetting(col);
				tabObject.push({ name: col.name, displayName: col.displayName, fields: this.buildMetaSetting(col, col.name) });
			} else if (col.type === 'table') {
				dataObject[col.name] = this.buildMetaSetting(col);
			}
		}
		this.metaFieldTabsSetting = tabObject;
		return dataObject;
	}
	resetPassword() {
		const dialogRef = this.dialog.open(ConfirmEntityDialogComponent, {
			width: '300px',
			data: {
				title: this.translate.instant('Reset Password'),
				description: this.translate.instant('Are you sure you want to reset this user password?'),
				cancelbtn: this.translate.instant('Close'),
				confirmbtn: this.translate.instant('Reset')
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				console.log('result', result);
				if (result) {
					this.confirmResetPassword();
				}
			}
		});
	}
	confirmResetPassword() {
		if (!this.loading) {
			this.loading = true;
			this.errorMessage = '';
			this.requestService.resetUserPassword(this.dialogData?.['_id'], (data, error) => {
				if (error) {
					this.errorMessage = error;
					this.layoutUtilsService.showNotification(this.translate.instant('Error:') + error, this.translate.instant('Dismiss'));
				}
				if (data) {
					this.layoutUtilsService.showNotification(this.translate.instant('Password Reset Confirmed'), this.translate.instant('Dismiss'));
				}
				this.loading = false;
			});
		}
	}
	public buildTabsMetaSetting() {
		if (this.confirmData) {
			for (let tab of this.metaFieldTabsSetting) {
				if (!this.tableDataTemp.hasOwnProperty(tab.name)) {
					if (this.confirmData.hasOwnProperty(tab.name)) {
						delete this.confirmData[tab.name];
						// Also update data.confirmData for backward compatibility
						if (this.data.confirmData) {
							delete this.data.confirmData[tab.name];
						}
					}
				}
				if (this.confirmData.hasOwnProperty(tab.name) || this.confirmData['role']?.['targetfield'] === tab.name) {
					if (!this.confirmData.hasOwnProperty(tab.name)) {
						this.confirmData[tab.name] = {};
						// Also update data.confirmData for backward compatibility
						if (this.data.confirmData) {
							this.data.confirmData[tab.name] = {};
						}
					}
				}
			}
		}
	}
	public viewTranscript(transcript: any) {
		window.open(transcript.fileLink, '_blank');
	}
	changeDND(event) {
		console.log(event.checked);
		localStorage.setItem("DND", JSON.stringify(event.checked))
	}

	editProfile() {
		const dialogRef = this.dialog.open(ModalDialogComponent, {
			width: "850px",
			data: {
				dataType: 'user',
				dataTypeTitle: 'User',
				title: this.translate.instant("Edit") + " " + 'User',
				data: { _id: this.dialogData?.['_id'] },
				modalSetting: this.getSetting({}),
				profile: this.isProfile,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (result && result.action === 'refresh') {
				if (result.data) {
					this.requestService.updateCommonUserData(result.data);
				}
			}
			// setTimeout(function () {
			// 	window.location.reload();
			// }, 300);

		});

	}
	protected getSetting(data) {
		let tableSetting = data;
		tableSetting['target'] = 'self';

		tableSetting['customSettings'] = {
			organizationId: { visible: false },
			appId: { visible: false },
			locationId: { visible: false },
			resources: { visible: false },
			ref_id: { disabled: true }
		};
		if (this.requestService.checkListContains(this.dialogData?.['resources'], environment.customKeys.roleClient)) {
			tableSetting['customSettings']['ref_id']['visible'] = true;
		} else {
			tableSetting['customSettings']['ref_id']['visible'] = false;
		}
		return tableSetting;
	}
	editProfileImage() {
		// console.log(this.data);
		const dialogRef = this.dialog.open(ModalCropDialogComponent, {
			width: '800px',
			disableClose: false,
			autoFocus: false,
			data: {
				title: 'Upload image',
				data: this.dialogData,
				currentImage: this.pictureLink,
				profile: this.isProfile
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result || result === '') {
				const newLink = result === '' ? 'assets/images/profile.png' : result;
				// Defer src change to next microtask to avoid NG0100 in this dialog
				queueMicrotask(() => {
					this.pictureLink = newLink;
					this.changeDetectorRefs.detectChanges();
				});
			}
		});
	}
}
